// Event venue address helpers. Mirror of
// server/src/helpers/eventPlanning/eventAddress.js — keep the two in step.

export const EVENT_ADDRESS_FIELDS = [
  ['lineOne', 'Address Line One'],
  ['lineTwo', 'Address Line Two'],
  ['town', 'Town'],
  ['county', 'County'],
  ['postCode', 'Post Code'],
]

const clean = (value) =>
  typeof value === 'string' && value.trim() ? value.trim() : ''

export const eventAddressCoordinates = (address) => {
  const coords = address?.location?.coordinates
  if (!Array.isArray(coords) || coords.length !== 2) return null
  const [lng, lat] = coords.map(Number)
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null
  return [lng, lat]
}

/** Short label shown on shift lines: "Town POSTCODE" (falls back to line one). */
export const summariseEventAddress = (address) => {
  if (!address) return ''
  const area = clean(address.town) || clean(address.lineOne)
  return [area, clean(address.postCode)].filter(Boolean).join(' ')
}

/** Full single-line address for page headers and detail panels. */
export const formatEventAddress = (address) => {
  if (!address) return ''
  return EVENT_ADDRESS_FIELDS.map(([field]) => clean(address[field]))
    .filter(Boolean)
    .join(', ')
}

export const hasEventAddress = (address) =>
  !!address && EVENT_ADDRESS_FIELDS.some(([field]) => clean(address[field]))

/** Best display string for an event: full address, else the legacy short text. */
export const displayEventLocation = (event) =>
  formatEventAddress(event?.eventAddress) || event?.eventLocation || ''

/**
 * Map a geocoder option (from AddressAutocomplete / /pts/address-search)
 * onto the eventAddress shape, preserving any county the user typed.
 */
export const eventAddressFromLookup = (option, previous = {}) => ({
  lineOne: option.addressLine1 || option.name || '',
  lineTwo: option.addressLine2 || '',
  town: option.town || option.city || '',
  county: previous.county || '',
  postCode: option.postCode || '',
  location:
    Array.isArray(option.coordinates) && option.coordinates.length === 2
      ? { type: 'Point', coordinates: option.coordinates }
      : option.location || undefined,
  addressMeta: option.addressMeta || undefined,
})

/**
 * Build the create/edit payload fields. An empty address is sent as null so
 * the server clears it; the legacy `eventLocation` string is derived here.
 */
export const eventAddressPayload = (address) => {
  if (!hasEventAddress(address)) {
    return { eventAddress: null, eventLocation: '' }
  }
  return {
    eventAddress: address,
    eventLocation: summariseEventAddress(address),
  }
}
